import { useEffect } from 'react';

import { EditorStarterAsset } from '@/features/remotion-editor/editor/assets/assets';
import { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import { TrackType } from '@/features/remotion-editor/editor/state/types';
import {
  useTracks,
  useAllItems,
  useAssets,
} from '@/features/remotion-editor/editor/utils/use-context';

interface LogEditorStatePayload {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  assets: Record<string, EditorStarterAsset>;
}

const createDebouncedLogger = (delayMs = 400) => {
  let timerId: ReturnType<typeof setTimeout> | null = null;

  const schedule = (payload: LogEditorStatePayload) => {
    if (timerId) {
      clearTimeout(timerId);
    }

    timerId = setTimeout(() => {
      logEditorState(payload);
    }, delayMs);
  };

  const cancel = () => {
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  return { schedule, cancel };
};

const debouncedLogger = createDebouncedLogger();

export const logEditorStateDebounced = debouncedLogger.schedule;
export const cancelLogEditorStateDebounce = debouncedLogger.cancel;

//* 훅 밖에서도 쓸 수 있는 콘솔 디버그 함수
export const logEditorState = ({
  tracks,
  items,
  assets,
}: LogEditorStatePayload) => {
  console.groupCollapsed('[EditorStateConsoleLog] context debug');
  console.log('tracks', tracks);
  console.log('items', items);
  console.log('assets', assets);
  console.log('--------------------------------');
  tracks.forEach((track) => {
    console.log('track.metadata?.type -->', track.metadata?.type);
    console.log(
      'track.items -->',
      track.items.map((itemId) => items[itemId]),
    );
    track.items.forEach((itemId) => {
      const item = items[itemId];
      if (item?.type === 'captions') {
        console.log({ ...item, assetData: assets[item.assetId] });
      } else if (item?.type === 'video') {
        console.log({ ...item, assetData: assets[item.assetId] });
      } else if (item?.type === 'audio') {
        console.log({ ...item, assetData: assets[item.assetId] });
      } else {
        console.log(item);
      }
    });
  });
  console.groupEnd();
};

//* 훅 내부에서는 데이터만 가져와서 재사용
export const useEditorStateConsoleLog = () => {
  const { tracks } = useTracks();
  const { items } = useAllItems();
  const { assets } = useAssets();

  //* 0.4초 디바운스를 적용해 렌더 빈도 로그 폭주 방지
  useEffect(() => {
    logEditorStateDebounced({ tracks, items, assets });

    return () => {
      cancelLogEditorStateDebounce();
    };
  }, [tracks, items, assets]);
};
